import React, { useCallback, useEffect, useState } from 'react';

import { css } from '@compiled/react';

import type { IconSize } from '@/libraries/design/foundations/icon';
import { isValidLocale } from '@/utils/localization';

import { NavigationTabsList } from './navigation-tabs-list';

export type NavigationTabsAppearance = 'wide' | 'compact';
export type NavigationTabsLayout = 'stacked' | 'overflow';
export type NavigationTabDataType = {
	id: string;
	title: string;
	href: string;
	iconUrl?: string;
	iconSize?: IconSize;
	disableIconPadding?: boolean;
	tabComponent: React.ReactElement;
};
export type NavigationTabsSectionProps = {
	tabsData: NavigationTabDataType[];
	appearance?: NavigationTabsAppearance;
	layout?: NavigationTabsLayout;
	useHistoryRouting?: boolean;
	eyebrow?: string;
	showControls?: boolean;
};

const sectionCss = css({
	width: '100%',
	maxWidth: '100%',
	minWidth: 0,
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
});
const stackedSectionCss = css({
	padding: '20px 0',
	alignItems: 'center',
	gap: '24px',
});
const wideSectionCss = css({
	width: '1162px',
	paddingBlockEnd: '40px',
	marginInline: 'auto',
	boxSizing: 'border-box',
});
const contentCss = css({ width: '100%', minWidth: 0 });

const normalizePathname = (href: string) => {
	const parsed = new URL(href, window.location.origin);
	const segments = parsed.pathname.split('/');
	if (isValidLocale(segments[1] ?? '')) segments.splice(1, 1);
	const pathname = segments.join('/').replace(/\/+$/, '');
	return pathname || '/';
};
const isEligibleHistoryActivation = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
	const target = event.currentTarget.target.trim().toLowerCase();
	return (
		!event.defaultPrevented &&
		event.button === 0 &&
		!event.metaKey &&
		!event.altKey &&
		!event.ctrlKey &&
		!event.shiftKey &&
		(!target || target === '_self') &&
		!event.currentTarget.hasAttribute('download') &&
		new URL(href, window.location.origin).origin === window.location.origin
	);
};

export const NavigationTabsSection = ({
	tabsData,
	appearance = 'wide',
	layout = 'stacked',
	useHistoryRouting = false,
	eyebrow,
	showControls = layout === 'overflow',
}: NavigationTabsSectionProps) => {
	const [activeHref, setActiveHref] = useState<string>();
	const resolveActiveHref = useCallback(() => {
		const pathname = normalizePathname(window.location.href);
		return tabsData.find((tab) => {
			const destination = new URL(tab.href, window.location.origin);
			return (
				destination.origin === window.location.origin && normalizePathname(tab.href) === pathname
			);
		})?.href;
	}, [tabsData]);
	useEffect(() => {
		const synchronize = () => setActiveHref(resolveActiveHref());
		synchronize();
		window.addEventListener('popstate', synchronize);
		return () => window.removeEventListener('popstate', synchronize);
	}, [resolveActiveHref]);
	const onLinkClick = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
			if (!isEligibleHistoryActivation(event, href)) return;
			event.preventDefault();
			const destination = new URL(href, window.location.origin);
			window.history.pushState(
				null,
				'',
				`${destination.pathname}${destination.search}${destination.hash}`,
			);
			setActiveHref(resolveActiveHref());
		},
		[resolveActiveHref],
	);
	const activeTab = tabsData.find((tab) => tab.href === activeHref);
	return (
		<section
			id="navigation-tabs"
			css={[
				sectionCss,
				layout === 'stacked' && stackedSectionCss,
				appearance === 'wide' && layout === 'overflow' && wideSectionCss,
			]}
		>
			<NavigationTabsList
				tabsData={tabsData}
				appearance={appearance}
				layout={layout}
				eyebrow={eyebrow}
				showControls={showControls}
				activeHref={activeHref}
				onLinkClick={useHistoryRouting ? onLinkClick : undefined}
			/>
			<div css={contentCss}>{activeTab?.tabComponent}</div>
		</section>
	);
};
